import { Injectable } from '@angular/core';
import {
  BUTTON_TYPE,
  CUSTOM_BUTTON_TYPE,
  TAB_TYPE,
  TABVIEW_TYPE,
  ROOT_ACTION_BAR_TYPES,
  PAGE_TYPES,
  PAGE_LEVEL_IDENTIFIER_TYPES,
  CONTAINER_PREFIXES,
  PREFIXED_PAGE_CONTAINER_TYPES,
  DIRECT_PREFIX_TYPES,
  ScreenNode,
  ScreenNodeProperties,
  GeneratedNodeNames
} from 'src/app/base/screen-identifier.constants';
import { RegexPatternConstants } from 'src/app/base/regex-pattern.constants';

/**
 * Service for identifier validation and auto-generation in screen structures.
 * Provides utilities for converting labels to identifiers and CSS class names,
 * and for managing node naming within hierarchical screen trees.
 */
@Injectable({
  providedIn: 'root'
})
export class ScreenIdentifierService {
  /**
   * Converts a label to hyphen-separated lowercase format suitable for CSS class names.
   * @param label - The label string to convert.
   * @returns The formatted CSS class name string.
   */
  toStyleClass(label: string): string {
    if (!label) {
      return '';
    }

    const cleanLabel = label.replace(/[^a-zA-Z0-9\s\-_]/g, '');

    if (!cleanLabel.trim()) {
      return '';
    }

    const words = cleanLabel.split(/[\s\-_]+/).filter((word) => word.length > 0);

    if (words.length === 0) {
      return '';
    }

    return words.map((word) => word.toLowerCase()).join('-');
  }

  /**
   * Validates whether an identifier follows the required format.
   * Rules:
   * - Must start with a lowercase letter
   * - Only lowercase letters, numbers, and underscores are allowed
   * - Must not end with an underscore
   * @param identifier - The identifier string to validate.
   * @returns True if the identifier is valid or empty, false otherwise.
   */
  isValidIdentifierFormat(identifier: string): boolean {
    if (!identifier || identifier.trim() === '') {
      return true;
    }

    return RegexPatternConstants.identifier.test(identifier.trim());
  }

  /**
   * Validates whether a style class follows the required CSS class format.
   * Rules:
   * - Must start with a letter (A-Z or a-z)
   * - Can contain letters (A-Z, a-z), digits (0-9), hyphens (-), and underscores (_)
   * @param styleClass - The style class string to validate.
   * @returns True if the style class is valid or empty, false otherwise.
   */
  isValidStyleClassFormat(styleClass: string): boolean {
    if (!styleClass || styleClass.trim() === '') {
      return true;
    }

    return RegexPatternConstants.styleClass.test(styleClass.trim());
  }

  /** Map of node types to their unique configuration ID property keys */
  private readonly nodeTypeConfigIdMap: Record<string, keyof ScreenNodeProperties> = {
    queryview: 'queryViewConfigurationId',
    standarddatagrid: 'stdGridConfigurationId',
    childlist: 'childGridConfigurationId',
    customcomponent: 'customComponentConfigurationId'
  };

  /**
   * Extracts the unique identifier from a screen node.
   * For certain node types (queryView, standardDatagrid, childList, customComponent),
   * prioritizes the configuration ID stored in properties as it's unique per instance.
   * @param node - The screen node to extract the ID from.
   * @returns The node identifier string.
   */
  private getNodeId(node: ScreenNode): string {
    const nodeType = node?.type?.toLowerCase() || '';
    const props = node?.data?.properties;

    // For node types that can have multiple instances with the same SID,
    // use the unique configuration ID stored in properties
    if (props) {
      const configIdKey = this.nodeTypeConfigIdMap[nodeType];
      if (configIdKey && props[configIdKey]) {
        return props[configIdKey] as string;
      }
    }

    // Prioritize 'key' as it's the unique FancyTree identifier (UUID)
    // Fall back to data.id for tree dict structure, then id for other cases
    return node?.key || node?.data?.id || node?.id || '';
  }

  /**
   * Extracts the type from a screen node in lowercase format.
   * @param node - The screen node to extract the type from.
   * @returns The lowercase node type string.
   */
  private getNodeType(node: ScreenNode): string {
    return node?.type?.toLowerCase() || '';
  }

  /**
   * Finds the nearest parent node matching one of the specified types.
   * @param targetNodeId - The ID of the node to find the parent for.
   * @param nodes - Array of root nodes to search within.
   * @param parentTypes - Array of parent type strings to match against.
   * @returns The matching parent node or null if not found.
   */
  findParentOfType(targetNodeId: string, nodes: ScreenNode[], parentTypes: string[]): ScreenNode | null {
    if (!targetNodeId || !nodes || !Array.isArray(nodes)) {
      return null;
    }

    for (const node of nodes) {
      const result = this.findParentOfTypeRecursive(targetNodeId, node, null, parentTypes);
      if (result) {
        return result;
      }
    }

    return null;
  }

  /**
   * Recursively searches for a parent node of the specified types.
   * @param targetNodeId - The ID of the target node.
   * @param currentNode - The current node being examined.
   * @param currentMatchingAncestor - The most recent matching ancestor found.
   * @param parentTypes - Array of parent type strings to match against.
   * @returns The matching parent node or null if not found.
   */
  private findParentOfTypeRecursive(
    targetNodeId: string,
    currentNode: ScreenNode,
    currentMatchingAncestor: ScreenNode | null,
    parentTypes: string[]
  ): ScreenNode | null {
    if (!currentNode) {
      return null;
    }

    const nodeId = this.getNodeId(currentNode);
    const nodeType = this.getNodeType(currentNode);

    if (parentTypes.includes(nodeType)) {
      currentMatchingAncestor = currentNode;
    }

    if (nodeId === targetNodeId) {
      return currentMatchingAncestor;
    }

    if (currentNode.children && Array.isArray(currentNode.children)) {
      for (const child of currentNode.children) {
        const result = this.findParentOfTypeRecursive(targetNodeId, child, currentMatchingAncestor, parentTypes);
        if (result) {
          return result;
        }
      }
    }

    return null;
  }

  /**
   * Finds the parent page node for a given node.
   * @param targetNodeId - The ID of the node to find the parent page for.
   * @param nodes - Array of root nodes to search within.
   * @returns The parent page node or null if not found.
   */
  findParentPage(targetNodeId: string, nodes: ScreenNode[]): ScreenNode | null {
    return this.findParentOfType(targetNodeId, nodes, PAGE_TYPES);
  }

  /**
   * Finds the parent TabView node for a tab.
   * @param targetNodeId - The ID of the tab node.
   * @param nodes - Array of root nodes to search within.
   * @returns The parent TabView node or null if not found.
   */
  findParentTabView(targetNodeId: string, nodes: ScreenNode[]): ScreenNode | null {
    return this.findParentOfType(targetNodeId, nodes, [TABVIEW_TYPE]);
  }

  /**
   * Finds the parent container that requires a prefix for page-level nodes.
   * @param targetNodeId - The ID of the node to find the prefixed container for.
   * @param nodes - Array of root nodes to search within.
   * @returns The prefixed parent container node or null if not found.
   */
  findPrefixedParentContainer(targetNodeId: string, nodes: ScreenNode[]): ScreenNode | null {
    return this.findParentOfType(targetNodeId, nodes, PREFIXED_PAGE_CONTAINER_TYPES);
  }

  /**
   * Gets the prefix for a page-level node based on its parent container type.
   * @param containerNode - The container node to extract the prefix from.
   * @returns The prefix string from the container prefixes map.
   */
  getPageLevelContainerPrefix(containerNode: ScreenNode | null): string {
    if (!containerNode) {
      return '';
    }
    const nodeType = this.getNodeType(containerNode);
    if (nodeType in CONTAINER_PREFIXES) {
      return CONTAINER_PREFIXES[nodeType];
    }
    return '';
  }

  /**
   * Determines the prefix and container node for a page-level node.
   * @param nodeId - The ID of the node to find the prefix for.
   * @param allNodes - Array of all nodes for hierarchy traversal.
   * @returns Object containing the prefix string and container node.
   */
  getPageLevelNodePrefix(nodeId: string, allNodes: ScreenNode[]): { prefix: string; containerNode: ScreenNode | null } {
    const containerNode = this.findPrefixedParentContainer(nodeId, allNodes);
    if (!containerNode) {
      return { prefix: '', containerNode: null };
    }
    const prefix = this.getPageLevelContainerPrefix(containerNode);
    return { prefix, containerNode };
  }

  /**
   * Finds the root action bar node for a given button node.
   * @param targetNodeId - The ID of the button node.
   * @param nodes - Array of root nodes to search within.
   * @returns The root action bar node or null if not found.
   */
  findRootActionBar(targetNodeId: string, nodes: ScreenNode[]): ScreenNode | null {
    return this.findParentOfType(targetNodeId, nodes, ROOT_ACTION_BAR_TYPES);
  }

  /**
   * Gets the action bar prefix based on the action bar node type.
   * @param actionBarNode - The action bar node to extract the prefix from.
   * @returns The prefix string for the action bar.
   */
  getActionBarPrefix(actionBarNode: ScreenNode | null): string {
    if (!actionBarNode) {
      return CONTAINER_PREFIXES['actionbar'];
    }

    const nodeTitle = actionBarNode.title?.toLowerCase().replace(/\s+/g, '') || '';

    if (nodeTitle in CONTAINER_PREFIXES && CONTAINER_PREFIXES[nodeTitle] !== '') {
      return CONTAINER_PREFIXES[nodeTitle];
    }

    return CONTAINER_PREFIXES['actionbar'];
  }

  /**
   * Determines the context node for prefix resolution based on node type.
   * @param node - The node requiring context resolution.
   * @param allNodes - Array of all nodes for hierarchy traversal.
   * @returns The context node for prefix determination or null.
   */
  private getContextNode(node: ScreenNode, allNodes: ScreenNode[]): ScreenNode | null {
    const nodeType = this.getNodeType(node);
    const nodeId = this.getNodeId(node);

    if (PAGE_LEVEL_IDENTIFIER_TYPES.includes(nodeType)) {
      return this.findParentPage(nodeId, allNodes);
    }

    if (nodeType === BUTTON_TYPE || nodeType === CUSTOM_BUTTON_TYPE) {
      return this.findRootActionBar(nodeId, allNodes);
    }

    return null;
  }

  /**
   * Generates identifier and styleClass for a node based on its context.
   * @param node - The screen node to generate names for.
   * @param allNodes - Array of all nodes for context resolution.
   * @param label - Optional label to use for generation.
   * @param parentNode - Optional parent node for additional context.
   * @returns Object containing generated identifier and styleClass.
   */
  generateIdentifierAndStyleClass(
    node: ScreenNode,
    allNodes: ScreenNode[],
    label?: string | null,
    parentNode?: ScreenNode | null
  ): GeneratedNodeNames {
    const nodeType = this.getNodeType(node);
    if (!nodeType) {
      return { dataKey: '', elementId: '', styleClass: '' };
    }

    let result: GeneratedNodeNames;

    // For all node types (including form fields and action bar buttons), use suffix-based deduplication scoped to page
    result = this.generatePageLevelNodeIdentifier(node, allNodes, label ?? undefined, parentNode ?? undefined);

    return result;
  }

  /**
   * Checks if the node type is an action bar child node that uses suffix-based identifiers.
   * @param nodeType - The lowercase type of the node.
   * @returns True if the node is an action bar child node.
   */
  private isActionBarChildNode(nodeType: string): boolean {
    return nodeType === BUTTON_TYPE || nodeType === CUSTOM_BUTTON_TYPE;
  }

  /**
   * Generates identifier and styleClass for action bar nodes using suffix-based deduplication.
   * @param node - The screen node to generate names for.
   * @param allNodes - Array of all nodes for context resolution.
   * @param label - The label to use for generation.
   * @returns Object containing generated identifier and styleClass with suffix if needed.
   */
  private generateActionBarNodeIdentifier(
    node: ScreenNode,
    allNodes: ScreenNode[],
    label?: string
  ): GeneratedNodeNames {
    if (!label?.trim()) {
      return { dataKey: '', elementId: '', styleClass: '' };
    }

    const baseIdentifier = this.toSnakeCase(label);
    if (!baseIdentifier || !this.isValidIdentifierFormat(baseIdentifier)) {
      return { dataKey: '', elementId: '', styleClass: '' };
    }

    const baseStyleClass = this.toStyleClass(label);
    const nodeId = this.getNodeId(node);
    const actionBarNode = this.findRootActionBar(nodeId, allNodes);

    // Collect existing identifiers from siblings in the same action bar
    const existingIdentifiers = this.collectActionBarIdentifiers(actionBarNode, nodeId);

    // Find unique suffix
    const suffix = this.findUniqueSuffix(baseIdentifier, existingIdentifiers);

    const dataKey = suffix === 0 ? baseIdentifier : `${baseIdentifier}_${suffix}`;

    return { dataKey, elementId: dataKey, styleClass: baseStyleClass };
  }

  /**
   * Collects all existing identifiers from nodes within an action bar.
   * @param actionBarNode - The root action bar node.
   * @param excludeNodeId - The node ID to exclude from collection (the current node).
   * @returns Set of existing identifier strings.
   */
  private collectActionBarIdentifiers(actionBarNode: ScreenNode | null, excludeNodeId: string): Set<string> {
    const identifiers = new Set<string>();

    if (!actionBarNode?.children) {
      return identifiers;
    }

    this.collectIdentifiersRecursive(actionBarNode.children, excludeNodeId, identifiers);
    return identifiers;
  }

  /**
   * Generates identifier and styleClass for page-level nodes using suffix-based deduplication.
   * @param node - The screen node to generate names for.
   * @param allNodes - Array of all nodes for context resolution.
   * @param label - The label to use for generation.
   * @param parentNode - Optional parent node for when the node is not yet in the tree.
   * @returns Object containing generated identifier, elementId and styleClass with suffix if needed.
   */
  private generatePageLevelNodeIdentifier(
    node: ScreenNode,
    allNodes: ScreenNode[],
    label?: string,
    parentNode?: ScreenNode
  ): GeneratedNodeNames {
    if (!label?.trim()) {
      return { dataKey: '', elementId: '', styleClass: '' };
    }

    const baseIdentifier = this.toSnakeCase(label);
    if (!baseIdentifier || !this.isValidIdentifierFormat(baseIdentifier)) {
      return { dataKey: '', elementId: '', styleClass: '' };
    }

    const baseStyleClass = this.toStyleClass(label);
    const nodeId = this.getNodeId(node);
    const nodeType = this.getNodeType(node);
    // Try to find parent page from the node's ID; if not found (new node), use parent node's context
    let pageNode = this.findParentPage(nodeId, allNodes);
    if (!pageNode && parentNode) {
      const parentId = this.getNodeId(parentNode);
      pageNode = this.findParentPage(parentId, allNodes);
    }

    // Determine prefix based on node type
    const prefix = this.getNodeTypePrefix(nodeType);

    // Collect existing identifiers from the page scope (excluding action bar containers)
    // Node types with unique configuration IDs (queryview, standarddatagrid, childlist, customcomponent)
    // are properly excluded by their configuration ID via getNodeId()
    const existingIdentifiers = this.collectPageLevelIdentifiers(pageNode, nodeId, allNodes);

    // Find unique suffix using prefixed identifier for deduplication
    const prefixedBaseIdentifier = prefix ? `${prefix}_${baseIdentifier}` : baseIdentifier;
    const suffix = this.findUniqueSuffix(prefixedBaseIdentifier, existingIdentifiers);

    const dataKey = suffix === 0 ? prefixedBaseIdentifier : `${prefixedBaseIdentifier}_${suffix}`;
    const styleClass = suffix === 0 ? baseStyleClass : `${baseStyleClass}-${suffix}`;

    return { dataKey, elementId: dataKey, styleClass };
  }

  /**
   * Gets the prefix based on node type.
   * @param nodeType - The lowercase type of the node.
   * @returns The prefix string (e.g., 'qf' for filterfield, 'tbl_srch' for searchfield, 'cb' for captionitem).
   */
  private getNodeTypePrefix(nodeType: string): string {
    switch (nodeType) {
      case 'filterfield':
        return 'qf';
      case 'searchfield':
        return 'tbl_srch';
      case 'captionitem':
        return 'cb';
      default:
        return '';
    }
  }

  /**
   * Collects all existing identifiers from page-level nodes (excluding action bar containers).
   * @param pageNode - The root page node.
   * @param excludeNodeId - The node ID to exclude from collection (the current node).
   * @param allNodes - Array of all nodes for context (includes siblings being added in batch).
   * @returns Set of existing identifier strings.
   */
  private collectPageLevelIdentifiers(
    pageNode: ScreenNode | null,
    excludeNodeId: string,
    allNodes: ScreenNode[]
  ): Set<string> {
    const identifiers = new Set<string>();

    if (pageNode?.children) {
      this.collectPageLevelIdentifiersRecursive(pageNode.children, excludeNodeId, identifiers);
    }

    // Also collect identifiers from allNodes recursively to capture siblings added in the same batch
    // and nested nodes from tree structure
    this.collectPageLevelIdentifiersRecursive(allNodes, excludeNodeId, identifiers);

    return identifiers;
  }

  /**
   * Recursively collects identifiers from page-level nodes.
   * Action bar container nodes are traversed but their identifiers are not collected.
   * Collects both dataKey and elementId to detect collisions when nodes are renamed.
   * @param nodes - Array of nodes to process.
   * @param excludeNodeId - The node ID to exclude.
   * @param identifiers - Set to add identifiers to.
   */
  private collectPageLevelIdentifiersRecursive(
    nodes: ScreenNode[],
    excludeNodeId: string,
    identifiers: Set<string>
  ): void {
    for (const node of nodes) {
      const nodeType = this.getNodeType(node);
      const nodeId = this.getNodeId(node);
      const dataKey = node.data?.properties?.dataKey;
      const elementId = node.data?.properties?.elementId;

      // For action bar container nodes, traverse children but don't collect their identifier
      if (ROOT_ACTION_BAR_TYPES.includes(nodeType)) {
        if (node.children?.length) {
          this.collectPageLevelIdentifiersRecursive(node.children, excludeNodeId, identifiers);
        }
        continue;
      }

      if (nodeId !== excludeNodeId) {
        if (dataKey) {
          identifiers.add(dataKey.toLowerCase());
        }
        // Also collect elementId to detect collisions when nodes are renamed
        if (elementId) {
          identifiers.add(elementId.toLowerCase());
        }
      }

      if (node.children?.length) {
        this.collectPageLevelIdentifiersRecursive(node.children, excludeNodeId, identifiers);
      }
    }
  }

  /**
   * Recursively collects identifiers from nodes.
   * Collects both dataKey and elementId to detect collisions when nodes are renamed.
   * @param nodes - Array of nodes to process.
   * @param excludeNodeId - The node ID to exclude.
   * @param identifiers - Set to add identifiers to.
   */
  private collectIdentifiersRecursive(nodes: ScreenNode[], excludeNodeId: string, identifiers: Set<string>): void {
    for (const node of nodes) {
      const nodeId = this.getNodeId(node);
      if (nodeId !== excludeNodeId) {
        const dataKey = node.data?.properties?.dataKey;
        const elementId = node.data?.properties?.elementId;
        if (dataKey) {
          identifiers.add(dataKey.toLowerCase());
        }
        // Also collect elementId to detect collisions when nodes are renamed
        if (elementId) {
          identifiers.add(elementId.toLowerCase());
        }
      }

      if (node.children?.length) {
        this.collectIdentifiersRecursive(node.children, excludeNodeId, identifiers);
      }
    }
  }

  /**
   * Finds a unique suffix for the base identifier.
   * @param baseIdentifier - The base identifier without suffix.
   * @param existingIdentifiers - Set of existing identifiers (lowercase).
   * @returns The suffix number (0 means no suffix needed).
   */
  private findUniqueSuffix(baseIdentifier: string, existingIdentifiers: Set<string>): number {
    const lowerBase = baseIdentifier.toLowerCase();

    // Check if base identifier is available
    if (!existingIdentifiers.has(lowerBase)) {
      return 0;
    }

    // Find next available suffix starting from 1
    let suffix = 1;
    while (existingIdentifiers.has(`${lowerBase}_${suffix}`)) {
      suffix++;
    }

    return suffix;
  }

  /**
   * Core generation method that creates identifier, elementId and styleClass from label with optional prefix.
   * @param prefix - The prefix to prepend to generated names.
   * @param label - The label to use for generation.
   * @returns Object containing generated identifier, elementId and styleClass.
   */
  private generateWithPrefix(prefix: string, label?: string): GeneratedNodeNames {
    if (!label?.trim()) {
      return { dataKey: '', elementId: '', styleClass: '' };
    }

    return this.generateFromLabel(prefix, label);
  }

  /**
   * Generates identifier, elementId and styleClass from a label string.
   * @param prefix - The prefix to prepend to generated names.
   * @param label - The label to convert.
   * @returns Object containing generated identifier, elementId and styleClass.
   */
  private generateFromLabel(prefix: string, label: string): GeneratedNodeNames {
    const baseIdentifier = this.toSnakeCase(label);
    if (!baseIdentifier || !this.isValidIdentifierFormat(baseIdentifier)) {
      return { dataKey: '', elementId: '', styleClass: '' };
    }

    const baseStyleClass = this.toStyleClass(label);
    const dataKey = prefix ? `${prefix}_${baseIdentifier}` : baseIdentifier;
    const styleClass = prefix ? `${prefix}-${baseStyleClass}` : baseStyleClass;

    return { dataKey, elementId: dataKey, styleClass };
  }

  /**
   * Converts a label to snake_case format.
   * @param label - The label string to convert.
   * @returns The snake_case formatted string.
   */
  toSnakeCase(label: string): string {
    return label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/(?:^_+)|(?:_+$)/g, '')
      .replace(/_+/g, '_');
  }

  /**
   * Extracts the title from a node object.
   * Priority: nodeObj.title (user-visible name in tree) > props.label (fallback)
   * @param nodeObj - The screen node to extract the title from.
   * @returns The extracted title string, or null if not found.
   */
  getNodeTitle(nodeObj: ScreenNode | null): string | null {
    if (!nodeObj) {
      return null;
    }

    // Primary: nodeObj.title - what user sees/types in the tree
    if (nodeObj.title?.trim()) {
      return nodeObj.title.trim();
    }

    // Fallback: props.label
    const props = nodeObj.data?.properties;
    if (props?.label?.trim()) {
      return props.label.trim();
    }

    return null;
  }

  /**
   * Converts a partial node object to ScreenNode for type-safe operations.
   * @param obj - The source node object.
   * @param label - Optional label to include in properties.
   * @returns A new ScreenNode with standardized structure.
   */
  private toScreenNode(obj: ScreenNode, label?: string | null): ScreenNode {
    // Prioritize key over id since key is the unique identifier in FancyTree
    const nodeKey = obj.key || obj.data?.id || obj.id;
    return {
      type: obj.type,
      key: nodeKey,
      data: {
        id: nodeKey,
        properties: { ...obj.data?.properties, ...(label ? { label } : {}) }
      }
    };
  }

  /**
   * Ensures that data.properties exists on a node, creating it if necessary.
   * @param node - The node to ensure properties exist on.
   * @returns The node's properties object.
   */
  private ensurePropertiesExist(node: ScreenNode): ScreenNodeProperties {
    if (!node.data) {
      node.data = {};
    }
    if (!node.data.properties) {
      node.data.properties = {};
    }
    return node.data.properties;
  }

  /**
   * Assigns identifier, elementId and styleClass to a node object based on its label and context.
   * @param nodeObj - The node object to assign properties to.
   * @param currentNodes - Array of all current nodes for context resolution.
   * @param parentNode - Optional parent node for additional context.
   * @returns The modified node object with assigned properties.
   */
  assignHtmlPropsToNode(nodeObj: ScreenNode, currentNodes: ScreenNode[], parentNode?: ScreenNode): ScreenNode {
    if (!nodeObj?.type) {
      return nodeObj;
    }

    const props = this.ensurePropertiesExist(nodeObj);
    const title = this.getNodeTitle(nodeObj);

    if (!props.dataKey || !props.elementId || !props.styleClass) {
      const screenNode = this.toScreenNode(nodeObj, title);
      const parentScreenNode = parentNode ? this.toScreenNode(parentNode) : null;

      const { dataKey, elementId, styleClass } = this.generateIdentifierAndStyleClass(
        screenNode,
        currentNodes,
        title,
        parentScreenNode
      );

      if (!props.dataKey && dataKey) {
        props.dataKey = dataKey;
      }

      if (!props.elementId && elementId) {
        props.elementId = elementId;
      }

      if (this.isStyleClassEmpty(props.styleClass) && styleClass) {
        props.styleClass = [styleClass];
      }
    }

    if (Array.isArray(nodeObj.children)) {
      this.assignHtmlPropsToChildren(nodeObj, currentNodes);
    }

    return nodeObj;
  }

  /**
   * Checks if a styleClass array is empty or not defined.
   * @param styleClass - The styleClass array to check.
   * @returns True if empty or not defined, false otherwise.
   */
  private isStyleClassEmpty(styleClass: string[] | null | undefined): boolean {
    return !styleClass || styleClass.length === 0;
  }

  /**
   * Assigns identifier, elementId and styleClass properties to child nodes with context-aware generation.
   * @param nodeObj - The parent node whose children need properties assigned.
   * @param currentNodes - Array of all current nodes for context resolution.
   */
  private assignHtmlPropsToChildren(nodeObj: ScreenNode, currentNodes: ScreenNode[]): void {
    if (!nodeObj.children) {
      return;
    }

    // Create a mutable copy of currentNodes to track siblings as they're processed
    const workingNodes = [...currentNodes];

    // Add parent node to working nodes so children can detect name conflicts with parent
    // This handles cases like tabView "Department" with child tab also named "Department"
    if (nodeObj.data?.properties?.dataKey) {
      workingNodes.push(nodeObj);
    }

    nodeObj.children = nodeObj.children.map((child) => {
      const childProps = this.ensurePropertiesExist(child);

      if ((!childProps.dataKey || !childProps.elementId || !childProps.styleClass) && child.type) {
        const childTitle = this.getNodeTitle(child);
        const childScreenNode = this.toScreenNode(child, childTitle);
        const { dataKey, elementId, styleClass } = this.generateIdentifierAndStyleClass(
          childScreenNode,
          workingNodes,
          childTitle,
          nodeObj
        );

        if (!childProps.dataKey && dataKey) {
          childProps.dataKey = dataKey;
        }

        if (!childProps.elementId && elementId) {
          childProps.elementId = elementId;
        }

        if (this.isStyleClassEmpty(childProps.styleClass) && styleClass) {
          childProps.styleClass = [styleClass];
        }

        // Add the processed child to workingNodes so siblings can detect duplicates
        workingNodes.push(child);
      }
      return child;
    });
  }

  /**
   * Updates elementId based on the node's label.
   * For fresh nodes (no identifier/styleClass yet), generates all three properties.
   * For existing nodes (already have identifier/styleClass), only updates elementId.
   * @param node - The screen node to update.
   * @param currentNodes - Array of all current nodes for context resolution.
   */
  updateHtmlPropsFromLabel(node: ScreenNode, currentNodes: ScreenNode[]): void {
    const props = node?.data?.properties;
    if (!props) {
      return;
    }

    // Priority: node.title (user-visible name) > props.label (fallback)
    const title = (node.title || props.label)?.trim();
    if (!title) {
      return;
    }

    const screenNode = this.toScreenNode(node, title);
    const { dataKey, elementId, styleClass } = this.generateIdentifierAndStyleClass(screenNode, currentNodes, title);

    // Check if this is a fresh node (no dataKey or styleClass set yet)
    const isFreshNode = !props.dataKey && this.isStyleClassEmpty(props.styleClass);

    if (isFreshNode) {
      // Fresh node with label set for the first time - generate all three
      if (dataKey) {
        props.dataKey = dataKey;
      }
      if (elementId) {
        props.elementId = elementId;
      }
      if (styleClass) {
        props.styleClass = [styleClass];
      }
    } else if (elementId) {
      // Existing node with label rename - only update elementId
      props.elementId = elementId;
    }
  }

  /**
   * Updates only the elementId when a field is renamed in data model.
   * dataKey and styleClass remain unchanged to preserve user customizations.
   *
   * @param node - The screen node to update (typically formField, gridColumn, searchField, filterField).
   * @param newFieldName - The new field name from the data model.
   * @param currentNodes - Array of all current nodes for context resolution.
   * @returns boolean indicating if elementId was updated.
   */
  updateHtmlPropsOnFieldRename(node: ScreenNode, newFieldName: string, currentNodes: ScreenNode[]): boolean {
    const props = node?.data?.properties;
    if (!props || !newFieldName?.trim()) {
      return false;
    }

    const title = newFieldName.trim();
    const screenNode = this.toScreenNode(node, title);
    const { elementId } = this.generateIdentifierAndStyleClass(screenNode, currentNodes, title);

    // Only update elementId for field rename - preserve dataKey and styleClass
    if (elementId && props.elementId !== elementId) {
      props.elementId = elementId;
      return true;
    }

    return false;
  }
}
